import React from 'react';
import { Box } from '@mui/material';
import { DischargeDisposition, DischargePatient } from '~/types/discharge';
import { buildProgramFit } from './buildProgramFit';
import DischargeProgramFitCard from './DischargeProgramFitCard';

interface Props {
  patient: DischargePatient;
  // The recommended program shows the "Recommended" chip on every patient.
  // The "Selected" chip is only shown once Predict is complete and a program
  // has been finalized — driven by this prop.
  selectedDisposition?: DischargeDisposition | null;
  // When provided, each card shows a Select button that calls back with the
  // chosen disposition. Omit to render the list read-only.
  onSelect?: (disposition: DischargeDisposition) => void;
}

const ProgramFitList: React.FC<Props> = ({ patient, selectedDisposition, onSelect }) => {
  const fit = React.useMemo(() => buildProgramFit(patient), [patient]);

  const ranked = (Object.keys(fit.scores) as DischargeDisposition[])
    .map(d => ({ disposition: d, score: fit.scores[d] ?? 0 }))
    .sort((a, b) => {
      // Selected disposition pinned to the top, then ranked by score.
      const aSel = selectedDisposition && a.disposition === selectedDisposition ? 1 : 0;
      const bSel = selectedDisposition && b.disposition === selectedDisposition ? 1 : 0;
      if (aSel !== bSel) return bSel - aSel;
      return b.score - a.score;
    });

  return (
    <Box>
      {ranked.map(({ disposition, score }, idx) => (
        <DischargeProgramFitCard
          key={disposition}
          disposition={disposition}
          score={score}
          decisionFactors={fit.decisionFactors[disposition] ?? []}
          isRecommended={disposition === fit.recommended}
          isSelected={!!selectedDisposition && disposition === selectedDisposition}
          defaultExpanded={idx === 0}
          onSelect={onSelect}
        />
      ))}
    </Box>
  );
};

export default ProgramFitList;
